import React from 'react';
import type { AtomMut } from '@reatom/framework';
import { atom } from '@reatom/framework';
import { useCtx } from '@reatom/npm-react';

import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef/index.ts';
import { getComponentDebugName } from '@/js/libs/reatom-devtools/index.ts';

import type { Observable } from './observable.ts';
import { createObservableFromAtom } from './observable.ts';

type SetState<T> = (callback: T | ((oldValue: T) => T)) => void;

export function useState$<T>(initialValue: T): [Observable<T>, SetState<T>] {
  const ref = React.useRef<AtomMut<T> | null>(null);
  // eslint-disable-next-line react-hooks/refs
  if (!ref.current) {
    ref.current = atom(initialValue, getComponentDebugName('useState$'));
  }
  const { current } = ref;

  const ctx = useCtx();
  const setState = useCallbackRef<SetState<T>>((v) => {
    if (typeof v === 'function') {
      // @ts-expect-error I wish we don't use a function like the value
      current(ctx, (value) => v(value));
    } else {
      current(ctx, v);
    }
  });

  // eslint-disable-next-line react-hooks/refs
  const observable = React.useMemo(() => createObservableFromAtom(current), [current]);

  return [observable, setState];
}
